import { AuthUserRole, User } from "@/shared/models/user"
import parsePhoneNumber, { parsePhoneNumberFromString } from "libphonenumber-js"
import { GgmsError } from "@/shared/services/client"
import { Prototype, Theme } from "@/shared/models/config"
import { Category } from "@/shared/models/category"
import { Attribute, AttributeType } from "@/shared/models/attributes"
import { Task } from "@/shared/models/task"
import { Note } from "@/shared/models/note"
import { UploadItem } from "@/shared/models/upload-file"
import { ScheduledMessageItem } from "@/shared/models/message-item"
import { Timeline, TimelineItem } from "@/shared/models/timelineItem"
import Mustache from "mustache"
import { Address, Agency, Url } from "@/shared/models/agency"
import { Contact } from "@/shared/models/contact"
import { Agent } from "@/shared/models/agent"
import { ScheduledEmailItem } from "@/shared/models/email"
import { AgencyTheme, ThemeColors } from "@/shared/models/agency-theme"
import { MessageTheme } from "@/shared/models/message-theme"
import { Permission, PermissionType, ResourceTypeName, Role } from "@/shared/models/role"
import { twMerge } from "tailwind-merge"
import {
    ContactActivityTriggerNodeType,
    ListingActivityTriggerNodeType,
    SendEmailToType,
} from "@/shared/models/workflow"
import { AlertTrigger } from "@/shared/models/collection"
import {
    AdjustmentsIcon,
    AtSymbolIcon,
    BellIcon,
    CalendarIcon,
    ChatAltIcon,
    ClipboardCheckIcon,
    ClipboardListIcon,
    CollectionIcon,
    CurrencyDollarIcon,
    FireIcon,
    GlobeAltIcon,
    HashtagIcon,
    HomeIcon,
    IdentificationIcon,
    LinkIcon,
    OfficeBuildingIcon,
    PaperClipIcon,
    PencilIcon,
    PhoneIcon,
    ShareIcon,
    SpeakerphoneIcon,
    TagIcon,
    UserCircleIcon,
    UserIcon,
} from "@heroicons/vue/outline"
import BulletListWithTwoCheckmarkIcon from "@/components/icons/BulletListWithTwoCheckmarkIcon.vue"
import NumberedListIcon from "@/components/icons/NumberedListIcon.vue"
import DNDIcon from "@/components/icons/DNDIcon.vue"
import { resetAuthState } from "@/shared/utils/auth-state"
import { FiltersGroup } from "@/shared/models/filter"
import { formatters } from "@/shared/utils/dateUtils"
import StringTypeIcon from "@/components/icons/StringTypeIcon.vue"
import BooleanTypeToggleIcon from "@/components/icons/BooleanTypeToggleIcon.vue"

export function stripHtml(html: string) {
    const doc = new DOMParser().parseFromString(html, "text/html")
    return doc.body.textContent || ""
}

export async function handleLoginResponse(token: string, user: User) {
    // Reset all auth state flags when a successful login occurs
    resetAuthState()

    setCookie("authToken", token, true)
    setCookie("user", JSON.stringify(user), true)
}

export function isPasswordValid(password: string) {
    return /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>])[A-Za-z\d!@#$%^&*(),.?":{}|<>]{8,}$/.test(password)
}

export function formatCallSenderReceiverData(from: Contact, to: Contact[]) {
    const fromDisplayName = getDisplayName(from)
    const fromContactInfo = getContactInfo(from)

    const toDisplayName = getDisplayName(to[0])
    const toContactInfo = getContactInfo(to[0])

    const senderInfo = fromDisplayName ? `${fromDisplayName} (${fromContactInfo})` : fromContactInfo
    const receiverInfo = toDisplayName ? `${toDisplayName} (${toContactInfo})` : toContactInfo

    return `${senderInfo} > ${receiverInfo}`
}

export function formatMessageSenderReceiverData(from: Contact, to: Contact[]) {
    const fromDisplayName = getDisplayName(from)
    const fromContactInfo = getContactInfo(from)

    const toDisplayName = getDisplayName(to[0])
    const toContactInfo = getContactInfo(to[0])

    const senderInfo = fromDisplayName ? `${fromDisplayName} (${fromContactInfo})` : fromContactInfo
    const receiverInfo = toDisplayName ? `${toDisplayName} (${toContactInfo})` : toContactInfo

    return `${senderInfo} > ${receiverInfo}`
}

export function getContactData(item: any, user: User) {
    const to = item.to?.[0]
    const from = item.from
    const SystemEmail = "development@ggms.com"

    if (to?._id === user._id && from?.email?.emailAddress !== SystemEmail) {
        return {
            ...(item.from?.firstName && { firstName: item.from?.firstName }),
            ...(item.from?.lastName && { lastName: item.from?.lastName }),
            ...(item.from?.fullName && { fullName: item.from?.fullName }),
            _id: item.from?._id,
            phone: item.from?.phone ?? { number: item.from.number },
            email: item.from?.email,
        }
    } else {
        return {
            ...(item.to?.[0]?.firstName && { firstName: item.to?.[0]?.firstName }),
            ...(item.to?.[0]?.lastName && { lastName: item.to?.[0]?.lastName }),
            ...(item.to?.[0]?.fullName && { fullName: item.to?.[0]?.fullName }),
            _id: item.to?.[0]?._id,
            phone: item.to?.[0]?.phone,
            email: item.to?.[0]?.email,
        }
    }
}

export function getDisplayName(contact: Contact | undefined): string | undefined {
    if (contact) {
        const { firstName, lastName, fullName } = contact

        if (firstName && lastName) {
            return `${firstName} ${lastName}`
        } else {
            return firstName || lastName || fullName
        }
    }
    return undefined
}

function getContactInfo(contact: Contact): string {
    if (contact.phone && contact.phone.number) {
        return contact.phone.number
    } else if (contact.email && contact.email.emailAddress) {
        return contact.email.emailAddress
    } else if (contact?.number) {
        return contact?.number
    } else {
        return "No contact information available"
    }
}

export function createAppointmentMessage(
    contactUserName: string,
    formattedDate: string,
    startTime: string,
    endTime: string
): string {
    let message = `Appointment for <b>${contactUserName}</b> will take place `
    if (formattedDate === "Today" || formattedDate === "Tomorrow") {
        message += `<b>${formattedDate}</b> from <b>${startTime}</b> to <b>${endTime}</b>`
    } else {
        message += `on <b>${formattedDate}</b> from <b>${startTime}</b> to <b>${endTime}</b>`
    }
    return message
}

export function clearStorage() {
    sessionStorage.clear()
    localStorage.clear()
    deleteAllCookies()
}

export function getUser(): User {
    const userJson = getCookieValue("user")
    return userJson ? JSON.parse(userJson) : null
}

export function getUserRole() {
    const user = getUser()
    return user?.role?.name || AuthUserRole.member
}

export function getUserPermission(role: Role, resource: ResourceTypeName): Permission {
    return (
        role?.permissions?.find((p: Permission) => [resource, "*"].includes(p.resource)) ||
        (role?.permissions?.[0] as Permission)
    )
}

export function getPermissionMask(permission: PermissionType): number {
    if (permission === PermissionType.READ) {
        return 0b100
    }
    if (permission === PermissionType.WRITE) {
        return 0b010
    }
    return 0b001
}

export function hasUserPermission(role: Role, resource: ResourceTypeName) {
    const userPermission = getUserPermission(role, resource)
    const mask = getPermissionMask(role?.permissions?.[0]?.type || PermissionType.READ)

    return (userPermission?.type & mask) === mask
}

export function hasUserOnlyReadPermission(pathName: string) {
    const user = getUser()
    if (!user?.role) return false

    const resource = modifyResourceName(pathName)?.toLowerCase() as ResourceTypeName
    const userPermission = getUserPermission(user?.role, resource)
    return getPermissionMask(userPermission?.type) === PermissionType.READ
}

export function modifyResourceName(resource: string) {
    if (resource === "contacts") {
        return "contact"
    }
    if (resource === "tasks") {
        return "task"
    }
    if (resource === "developers") {
        return "apiKey"
    }
    return resource
}

export function updateUser(user: Partial<User>) {
    const currentUser = getUser() ?? {}

    if (getCookieValue("user")) {
        setCookie("user", JSON.stringify({ ...currentUser, ...user }), true)
    }
}

export function getToken() {
    return getCookieValue("authToken")
}

export function isUserLoggedIn(): boolean {
    return !!getToken() && !!getUser()
}

export function debounce<Params extends any[]>(
    func: (...args: Params) => any,
    timeout: number
): (...args: Params) => void {
    let timer: ReturnType<typeof setTimeout>
    return (...args: Params) => {
        clearTimeout(timer)
        timer = setTimeout(() => {
            func(...args)
        }, timeout)
    }
}

export function wordToKeys(word: string) {
    const keyPads = [
        {
            alpha: ["A", "B", "C"],
            digit: 2,
        },
        {
            alpha: ["D", "E", "F"],
            digit: 3,
        },
        {
            alpha: ["G", "H", "I"],
            digit: 4,
        },
        {
            alpha: ["J", "K", "L"],
            digit: 5,
        },
        {
            alpha: ["M", "N", "O"],
            digit: 6,
        },
        {
            alpha: ["P", "Q", "R", "S"],
            digit: 7,
        },
        {
            alpha: ["T", "U", "V"],
            digit: 8,
        },
        {
            alpha: ["W", "X", "Y", "Z"],
            digit: 9,
        },
        {
            alpha: [" "],
            digit: 0,
        },
    ]
    return (
        word
            ?.trim()
            ?.toUpperCase()
            ?.split("")
            ?.map((letter) => {
                if (!letter) return ""
                if (!isNaN(parseInt(letter))) return letter
                const periodWord = keyPads.find((item) => item.alpha.includes(letter))
                if (!periodWord) {
                    return ""
                }
                return periodWord.digit.toString()
            })
            ?.join("") || ""
    )
}

export function internationalPhoneFormat(phoneString?: string) {
    if (!phoneString) return phoneString
    return parsePhoneNumber(phoneString)?.formatInternational() || phoneString
}

export function isInvalid(input: string, error: GgmsError) {
    return error?.field === input
}

export function hasErrorMessage(input: string, error: GgmsError) {
    return error?.field === input ? error?.message : undefined
}

export function capitalize(string: string) {
    if (!string || typeof string !== "string") return ""
    return string.charAt(0).toUpperCase() + string.slice(1)
}

export function isString(variable: any) {
    return typeof variable === "string"
}

export function deleteEmptyKeys(obj: any) {
    const tempObj = Object.assign({}, obj)
    for (const key in tempObj) {
        if (obj[key] === "") delete tempObj[key]
    }
    return tempObj
}

export function getInitials(name?: string): string {
    if (!name) return "XY"
    const splittedName = name.split(" ")
    return `${splittedName[0]?.[0] || ""}${splittedName[1]?.[0] || ""}`.toUpperCase()
}

export function getFullName(firstName?: string, lastName?: string, fullName?: string, fallbackEmail?: string): string {
    if (firstName && lastName) return `${firstName} ${lastName}`
    if (firstName) return firstName
    if (lastName) return lastName
    return fullName || fallbackEmail || "Unknown"
}

export function getFullNameWithoutUnknown(firstName?: string, lastName?: string, fullName?: string): string {
    if (firstName && lastName) return `${firstName} ${lastName}`
    if (firstName) return firstName
    if (lastName) return lastName
    return fullName || ""
}

export function formatPhoneNumber(phoneNumber: string) {
    const parsedPhoneNumber = parsePhoneNumberFromString(phoneNumber)

    if (parsedPhoneNumber) {
        return parsedPhoneNumber.formatInternational()
    }

    return null
}

export function isAdmin(): boolean {
    return getUserRole() === AuthUserRole.owner
}

export function getCopyArray(data: Array<any> | undefined) {
    return data?.slice() || []
}

export function objectsEqual(o1: any, o2: any): boolean {
    if (o1 === undefined && o2 === undefined) return true
    if ((o1 === undefined && o2 !== undefined) || (o1 !== undefined && o2 === undefined)) return false

    const o1Keys = Object.keys(o1)
    const o2Keys = Object.keys(o2)

    return typeof o1 === "object" && o1Keys.length
        ? o1Keys.length === o2Keys.length && o1Keys.every((p) => objectsEqual(o1[p], o2[p]))
        : o1 === o2
}

export function isArrayOfObjectsEqual(a1: Array<any>, a2: Array<any>) {
    a1 = a1 || []
    a2 = a2 || []

    return a1?.length === a2?.length && a1.every((o, idx) => objectsEqual(o, a2[idx]))
}

export function setDragCursor(value: boolean) {
    const html = document.getElementsByTagName("html").item(0)
    if (!html) return
    html.classList.toggle("grabbing", value)
}

export function hasOwn(object: Record<string, any>, property: string): boolean {
    return Object.prototype.hasOwnProperty.call(object, property)
}

function capitalizeWords(str: string): string {
    return str
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ")
}

export function getAttributesByCategories(
    prototype: Prototype,
    excludedAttributes?: string[],
    allowHidden?: boolean
): Partial<Category>[] {
    const allowedTypes = [
        "string",
        "date",
        "number",
        "object",
        "email[]",
        "phonenumber[]",
        "eventtime",
        "tag[]",
        "boolean",
        "group[]",
    ]
    const res = [] as Partial<Category>[]
    const custom = {
        attributes: [] as Attribute[],
        displayName: prototype?.displayName
            ? `${
                  prototype?.displayName.endsWith("s")
                      ? capitalizeWords(prototype?.displayName.slice(0, -1))
                      : capitalizeWords(prototype?.displayName)
              } Fields`
            : "Custom Fields",
        name: "custom",
    }
    prototype?.attributes.forEach((attribute) => {
        if (!allowHidden && attribute.isHidden) return
        if (attribute.name && excludedAttributes?.includes(attribute.name)) return
        if (!allowedTypes.includes(attribute?.type.toLowerCase())) return

        if (attribute?.category?.name) {
            const index = res.findIndex((category) => category.name === attribute.category?.name)
            if (index === -1) {
                res.push({ ...attribute?.category, attributes: [attribute] })
            } else {
                res[index].attributes?.push(attribute)
            }
        } else {
            custom.attributes.push(attribute)
        }
    })
    res.push(custom)
    return res.sort((a, b) => {
        if (a.displayName && b.displayName) {
            return a.displayName.localeCompare(b.displayName)
        }
        return 0
    })
}

export function isPinnedItem(elem: TimelineItem) {
    return (elem as TimelineItem)?.pinned !== undefined
}

export function isNote(elem: Timeline): elem is Note {
    return (elem as Note).message !== undefined
}

export function isTask(elem: Timeline): elem is Task {
    return (elem as Task).status !== undefined
}

export function isNoteFile(elem: Timeline): elem is UploadItem {
    return (elem as UploadItem)._id !== undefined
}

export function isContact(elem: Timeline): elem is Contact {
    return (elem as Contact).firstName !== undefined
}

export function isScheduledMessage(elem: Partial<ScheduledMessageItem>): elem is ScheduledMessageItem {
    return (
        (elem as ScheduledMessageItem).__t === "SMSMessagingThread" &&
        (elem as ScheduledMessageItem).scheduledAt !== undefined
    )
}

export function isScheduledEmail(elem: Partial<ScheduledEmailItem>): elem is ScheduledEmailItem {
    return (
        ((elem as ScheduledEmailItem).__t === "EmailMessagingThread" ||
            (elem as ScheduledEmailItem).__t === "EmailMessaging") &&
        (elem as ScheduledEmailItem).scheduledAt !== undefined
    )
}

export function createNameAndPhoneFormat(name: string, phone: { number: string }) {
    return `${name} ${internationalPhoneFormat(phone.number)}`
}

// split capitalized word to words
export function splitCamelCase(str: string) {
    return str.replace(/([a-z])([A-Z])/g, "$1 $2")
}

export function isPrimitiveArrayEqual(array1: any, array2: any) {
    if (array1.length === array2.length) {
        return array1.every((element: string | number) => array2.includes(element))
    }
    return false
}

// It transforms the template variables to the actual values
export function renderTemplate(
    body: string,
    prototypes: Prototype[],
    models: { contact: Contact; agency: Agency; agent: Agent },
    theme?: string,
    signature?: string
) {
    const view: { [key: string]: any } = {}

    prototypes
        .filter((prototype) => ["contact", "agency", "agent"].includes(prototype.typeName))
        .forEach((prototype) => {
            const currentModel = models[prototype.typeName as keyof { contact: Contact; agency: Agency; agent: Agent }]

            view[prototype.typeName] = prototype.attributes
                .filter(({ isHidden }) => !isHidden)
                .reduce((acc: any, current) => {
                    const name = current.name as keyof typeof currentModel

                    if ((current?.type === "string" || current?.type === "number") && current.name) {
                        return { ...acc, [current.name]: currentModel?.[name] || current.displayName }
                    }

                    if (current.name === "phone") {
                        return {
                            ...acc,
                            [current.name]: (currentModel as Contact)?.phones?.[0]?.number || current.displayName,
                        }
                    }

                    if (current.name === "phoneNumber") {
                        return {
                            ...acc,
                            [current.name]: (currentModel as Agent)?.phoneNumber?.number || current.displayName,
                        }
                    }

                    if (current.name === "agencyPhoneNumber") {
                        return {
                            ...acc,
                            [current.name]: (currentModel as Agency)?.agencyPhoneNumber?.number || current.displayName,
                        }
                    }

                    if (current.name === "agencyOutboundPhoneNumber") {
                        return {
                            ...acc,
                            [current.name]:
                                (currentModel as Agency)?.agencyOutboundPhoneNumber?.number || current.displayName,
                        }
                    }

                    return acc
                }, {})
        })

    if (signature) {
        view.templateRenderedSignature = Mustache.render(signature, view)
    }

    if (theme) {
        const { localBody: parsedBody, localView: parsedView } = mustacheParseMessagBody(body, view)
        parsedView.templateRenderedBody = Mustache.render(parsedBody, parsedView)
        return Mustache.render(theme, parsedView)
    }

    const { localBody: parsedBody, localView: parsedView } = mustacheParseMessagBody(body, view)
    return Mustache.render(parsedBody, parsedView)
}

function mustacheParseMessagBody(body: string, view: { [key: string]: any }) {
    let localBody = body
    const localView = structuredClone(view)

    Mustache.parse(localBody)
        .filter((bodyPart) => bodyPart[0] === "name")
        .forEach((bodyPart) => {
            const elem = bodyPart[1].toString()

            const [property, fallback] = elem.split(":")

            if (!fallback) {
                return
            }

            const fallbackValue = fallback.replace(/[^a-zA-Z0-9. ]/g, "")
            const typeName = property.split(".")[0]
            const attributeName = property.split(".")[1]

            if (camelCaseToWords(localView[typeName][attributeName]) === attributeName) {
                localView[typeName][attributeName] = fallbackValue
            }
            localBody = localBody.replace(`:${fallback}`, "")
        })

    return { localBody, localView }
}

export function camelCaseToWords(str?: string) {
    if (!str) return ""
    return str
        .replace(/(?:^\w|[A-Z]|\b\w)/g, function (word: string, index: number) {
            return index === 0 ? word.toLowerCase() : word.toUpperCase()
        })
        .replace(/\s+/g, "")
}

export function isAgencyTheme(elem: Theme): elem is AgencyTheme {
    return (elem as AgencyTheme).displayName === "Agency"
}

export function isThemeColor(elem: Theme): elem is ThemeColors {
    return (elem as ThemeColors).linkColor !== undefined
}

export function isMessageTheme(elem: Theme): elem is MessageTheme {
    return (elem as MessageTheme).body !== undefined
}

export function unsecuredCopyToClipboard(text: string) {
    const textArea = document.createElement("textarea")
    textArea.value = text
    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()
    try {
        document.execCommand("copy")
    } catch (err) {
        console.error("Unable to copy to clipboard", err)
    }
    document.body.removeChild(textArea)
}

export function copyToClipboard(text: string) {
    if (window.isSecureContext && navigator.clipboard) {
        navigator.clipboard.writeText(text)
    } else {
        unsecuredCopyToClipboard(text)
    }
}

export function getDomainId() {
    return (
        localStorage.getItem("contactDomainId") ||
        localStorage.getItem("taskDomainId") ||
        JSON.parse(getCookieValue("selectedDomain") || "{}")?._id
    )
}

export function getAgencyId() {
    return JSON.parse(getCookieValue("selectedAgency") || "{}")?._id
}

export function parseJwt(token: string) {
    const base64Url = token.split(".")[1]
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/")
    const jsonPayload = decodeURIComponent(
        window
            .atob(base64)
            .split("")
            .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
            .join("")
    )

    return JSON.parse(jsonPayload)
}

export function convertAddressToString(address: Address) {
    return `<p>${address?.line1} <br> ${address?.city}, ${address?.state} ${address.zipcode}, ${address.country}</p>`
}

export function getTaskTypes() {
    return [
        { attribute: "call", value: "Phone Call" },
        { attribute: "email", value: "Email" },
        { attribute: "sms", value: "Text Message" },
        { attribute: "todo", value: "To-do" },
    ]
}

export function getAppointmentTourTypes() {
    return [
        { attribute: "inPerson", value: "In Person" },
        { attribute: "virtual", value: "Virtual" },
    ]
}

export function getAppointmentReminderOptions() {
    return [
        { attribute: "minutes_30", value: "30 min before" },
        { attribute: "hours_1", value: "1 hour before" },
        { attribute: "days_1", value: "1 day before" },
        { attribute: "weeks_1", value: "1 week before" },
    ]
}

export function getRoleOptions() {
    return [
        { label: "Owner", value: "owner" },
        { label: "Member", value: "member" },
    ]
}

export function getPositionTitleOptions() {
    return [
        { label: "Broker/Owner", value: "brokerOrOwner" },
        { label: "Managing Broker", value: "managingBroker" },
        { label: "Team Leader", value: "teamLeader" },
        { label: "Real Estate Agent", value: "realEstateAgent" },
        { label: "Inside Sales Agent", value: "insideSalesAgent" },
        { label: "Transaction Coordinator", value: "transactionCoordinator" },
        { label: "Listing Coordinator", value: "listingCoordinator" },
        { label: "Marketing Manager", value: "marketingManager" },
        { label: "Client Success/Support", value: "clientSuccessSupport" },
        { label: "Office Admin", value: "officeAdmin" },
        { label: "Virtual Assistant", value: "virtualAssistant" },
        { label: "Showing Assistant", value: "showingAssistant" },
    ]
}

export function getTaskReminderOptions() {
    return [
        { attribute: "minutes_30", value: "30 min before" },
        { attribute: "hours_1", value: "1 hour before" },
        { attribute: "days_1", value: "1 day before" },
        { attribute: "weeks_1", value: "1 week before" },
    ]
}

export function isImageType(mimeType: string) {
    return ["image/jpeg", "image/png"].includes(mimeType)
}

export function isVideoType(mimeType: string) {
    return ["video/mp4", "video/mov"].includes(mimeType)
}

export function getCookieValue(name: string) {
    const env = process.env.VUE_APP_ENV
    const cookie = {} as { [key: string]: string }
    document.cookie.split(";").forEach((el) => {
        const [key, ...value] = el.split("=")
        cookie[key.trim()] = value.join("=")
    })
    return cookie[`${env}_${name}`]
}

export function setCookie(name: string, value: string, isForDomain = false, hasExpiration = true) {
    const env = process.env.VUE_APP_ENV
    let cookie = `${env}_${name}=${value}; Path=/; SameSite=None; Secure`

    if (isForDomain) {
        const currentHostname = window.location.hostname

        if (!isCustomDomain(currentHostname)) {
            if (!process.env.VUE_APP_BASE_WEB_URL) {
                return
            }
            // Internal domains use base domain (old logic)
            const baseUrl = new URL(process.env.VUE_APP_BASE_WEB_URL)
            const domain = window.location.hostname === "localhost" ? "localhost" : `.${baseUrl.hostname}`
            cookie += `; Domain=${domain}`
        } else {
            // Custom domain - set cookie for exact domain (no parent domain)
            cookie += `; Domain=${currentHostname}`
        }
    }

    if (hasExpiration) {
        const date = new Date()
        const cookieExpirationDate = new Date(date.setMonth(date.getMonth() + 1)).toUTCString()
        cookie += `; Expires=${cookieExpirationDate}`
    }

    document.cookie = cookie
}

export function deleteAllCookies() {
    const cookiesArray = document.cookie.split(";")

    const currentDomain = window.location.hostname === "localhost" ? "localhost" : window.location.hostname

    const baseDomain = process.env.VUE_APP_BASE_WEB_URL
        ? new URL(process.env.VUE_APP_BASE_WEB_URL).hostname
        : currentDomain

    cookiesArray.forEach((cookie) => {
        const [key] = cookie.split("=")

        document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${currentDomain}`

        if (baseDomain !== currentDomain) {
            document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${baseDomain}`
        }
    })
}

export function deleteCookie(name: string) {
    const env = process.env.VUE_APP_ENV
    if (!process.env.VUE_APP_BASE_WEB_URL) {
        return
    }
    const baseUrl = new URL(process.env.VUE_APP_BASE_WEB_URL)
    const domain = window.location.hostname === "localhost" ? "localhost" : `.${baseUrl.hostname}`
    document.cookie = `${env}_${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain}`
}

export function handleSubdomainRedirect(agency: Partial<Agency> | undefined, shouldRedirectToCMS = false) {
    const domains = agency?.agencyDomains
    if (!process.env.VUE_APP_BASE_WEB_URL || !domains?.length) {
        return
    }

    if (shouldRedirectToCMS) {
        return
    }

    setCookie("selectedAgency", JSON.stringify({ ...agency, mls: undefined }), true)

    const currentHostname = window.location.hostname
    const isCustomDomainLogin = getCookieValue("isCustomDomainLogin") === "true"

    if (isCustomDomain(currentHostname) || isCustomDomainLogin) {
        setCookie("isCustomDomainLogin", "false", true) // Clear after first use
        return // Stay here
    }

    if (isCustomDomain(currentHostname) || isCustomDomainLogin) {
        // Custom domain (crm.tnylink.io or crm.someagentdomain.com)
        // Direct match to agencyDomains
        const matchedDomain = domains.find((domain) => (domain.url as Url).crm?.dns === currentHostname)

        if (matchedDomain) {
            setCookie("selectedDomain", JSON.stringify(matchedDomain), true)
        }

        // No redirect — custom domains should "stay where they are"
        return
    }

    // Internal subdomain logic (old behavior)
    const currentSubDomain = currentHostname.split(".")[0]
    const matchedDomain = domains.find((domain) => domain.url === currentSubDomain)

    if (matchedDomain) {
        setCookie("selectedDomain", JSON.stringify(matchedDomain), true)
        return
    }

    if (domains.length > 1) {
        setCookie("selectedDomain", JSON.stringify({ _id: "all", url: "All domains" }), true)
        return
    }

    setCookie("selectedDomain", JSON.stringify(domains[0]), true)

    // Redirect to internal subdomain if needed
    const baseUrl = new URL(process.env.VUE_APP_BASE_WEB_URL)
    window.location.replace(`${baseUrl.protocol}//${domains[0].url}.${baseUrl.hostname}${baseUrl.pathname}`)
}

export function extractBareSubdomain(hostname: string): string {
    const parts = hostname.split(".")
    if (parts.length === 3) {
        // crm.tnylink.io -> tnylink-io
        return `${parts[1]}-${parts[0]}`
    }
    throw new Error(`Invalid custom domain format: ${hostname}`)
}

export function isCustomDomain(hostname: string): boolean {
    const internalSuffixes = [".localhost", ".dev.crm.ggms.com", ".staging.crm.ggms.com", ".crm.ggms.com"]

    return !internalSuffixes.some((suffix) => hostname.endsWith(suffix))
}

export function isValidDomain(domain: string) {
    const domainPattern = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)*[a-zA-Z0-9-]+\.[a-zA-Z]{2,}$/

    return domainPattern.test(domain)
}
// Helper function to decode HTML entities
function decodeHtmlEntities(str: string): string {
    const parser = new DOMParser()
    const decoded = parser.parseFromString(str, "text/html").documentElement.textContent
    return decoded || str
}

export function linkify(text: string) {
    const youtubeRegex = /https?:\/\/(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/i

    // Parse the text as HTML
    const parser = new DOMParser()
    const doc = parser.parseFromString(text, "text/html")

    // Process all links
    const links = doc.querySelectorAll("a")
    links.forEach((link) => {
        const href = link.getAttribute("href")
        const linkText = link.textContent?.trim() // Preserve and check for custom text

        if (href) {
            const decodedUrl = decodeHtmlEntities(href)

            if (youtubeRegex.test(decodedUrl)) {
                if (linkText) {
                    link.setAttribute("href", decodedUrl)
                    link.setAttribute("class", "underline text-link-color-500 hover:text-link-color-700")
                    link.setAttribute("target", "_blank")
                    link.setAttribute("rel", "noopener noreferrer nofollow")
                } else {
                    const youtubeMatch = youtubeRegex.exec(decodedUrl)
                    if (youtubeMatch) {
                        const videoId = youtubeMatch[3]
                        link.outerHTML = `
                            <a href="https://www.youtube.com/watch?v=${videoId}" target="_blank" rel="noopener noreferrer nofollow">
                                <img src="https://img.youtube.com/vi/${videoId}/hqdefault.jpg"
                                     alt="Watch the video on YouTube"
                                     style="max-width: 100%; height: auto; border: none; display: block;">
                            </a>`
                    }
                }
            } else {
                link.setAttribute("href", decodedUrl)
                link.setAttribute("class", "underline text-link-color-500 hover:text-link-color-700")
                link.setAttribute("target", "_blank")
                link.setAttribute("rel", "noopener noreferrer nofollow")
            }
        }
    })

    // Process all remaining links from the text
    const body = doc.body
    if (body) {
        const bodyText = body.textContent
        if (bodyText && bodyText.includes("http")) {
            const bodyTextWithLinks = bodyText.replace(
                /(https?:\/\/[^\s]+)/g,
                '<a href="$1" target="_blank" rel="noopener noreferrer nofollow" class="underline text-link-color-500 hover:text-link-color-700">$1</a>'
            )
            body.innerHTML = bodyTextWithLinks
        }
    }

    return doc.body.innerHTML
}

export function convertValue(value: string, isUsedForDisplay?: boolean): string {
    const valueMap: Record<string, string> = {
        call: "Phone Call",
        email: "Email",
        EmailMessagingThread: "Email",
        SMSMessagingThread: "Text Message",
        todo: "To-do",
        sms: "Text Message",
        "message-template": "Message Template",
        "contact-stage": "Contact Stage",
        minutes_30: "30 Minutes Before",
        hours_1: "1 Hour Before",
        days_1: "1 Day Before",
        weeks_1: "1 Week Before",
        unpublished: "Unpublished",
        off: "Off",
        on: "On",
    }

    return valueMap[value] || (isUsedForDisplay ? value : capitalize(value))
}

export function convertType(type?: string) {
    if (type === "Phone Call") return "call"
    if (type === "Email") return "email"
    if (type === "To-do") return "todo"
    if (type === "Text Message") return "sms"

    // types for tags
    if (type === "Message Template") return "message-template"
    if (type === "Contact Stage") return "contact-stage"

    return type?.toLowerCase()
}

export function convertTriggerEvent(event: string): string {
    const eventMap: Record<string, string> = {
        [ContactActivityTriggerNodeType.callStarted]: "Call Started",
        [ContactActivityTriggerNodeType.callEnded]: "Call Ended",
        [ContactActivityTriggerNodeType.propertyValueChanged]: "Contact Field Updated",
        [ContactActivityTriggerNodeType.membershipChanged]: "Collection Membership Changed",
        [ContactActivityTriggerNodeType.enrolledInWorkflow]: "Enrolled in Workflow",
        [ContactActivityTriggerNodeType.unenrollFromWorkflow]: "Unenrolled from Workflow",
        [ContactActivityTriggerNodeType.contactCreated]: "New Contact Added",
        [ContactActivityTriggerNodeType.submission]: "Form Submitted",
        [ContactActivityTriggerNodeType.view]: "Form Viewed",
        [ListingActivityTriggerNodeType.appointmentCreated]: "Appointment → Assigned to Team Member",
        [ListingActivityTriggerNodeType.appointmentCreatedAgency]: "Appointment → Unassigned / Agency Queue",
        [ListingActivityTriggerNodeType.pageVisited]: "Page Visited",
        [ListingActivityTriggerNodeType.requestForInformation]: "Information Requested",
        [ListingActivityTriggerNodeType.showingRequest]: "Showing Requested",
        [ListingActivityTriggerNodeType.saveListing]: "Listing Saved",
        [ListingActivityTriggerNodeType.hideListing]: "Listing Hidden",
    }
    return eventMap[event] ?? capitalize(event)
}

export function displayVisibility(value: string) {
    return value === "false" ? "Visible" : "Hidden"
}

export function displayFilterValue(
    value: string | number | boolean,
    field?: string,
    isDate?: boolean,
    shouldDisplayTime?: boolean,
    isType?: boolean,
    isUsedForDisplay?: boolean,
    isReminder?: boolean
) {
    if (field?.toLowerCase().includes("price") && typeof value === "string") {
        const num = Number(value.replace(/[^0-9.]/g, ""))
        if (!isNaN(num)) {
            return `$${num.toLocaleString("en-US")}`
        }
    }
    if (field === "doNotDisturb") {
        return value === "true" ? "is enabled" : "is not enabled"
    }

    if (field === "__t") {
        return convertValue(value as string)
    }

    if (typeof value === "boolean") {
        return value ? "Yes" : "No"
    }

    if (typeof value === "number") {
        return value
    }

    if (isType || isReminder) {
        return convertValue(value || "")
    }

    if (typeof value === "string") {
        switch (value) {
            case "__last_24_days__":
                return "Last 24 hours"
            case "__last_3_days__":
                return "Last 3 days"
            case "__last_7_days__":
                return "Last 7 days"
            case "__last_14_days__":
                return "Last 14 days"
            case "__last_30_days__":
                return "Last 30 days"
        }
    }

    if (isDate) {
        if (!value) return ""

        return formatters.formatHumanReadableDate(new Date(value), shouldDisplayTime || false)
    }

    return isUsedForDisplay ? capitalize(value?.replace(/\[,]/g, "")) : value?.replace(/\[,]/g, "")
}

export function displayFilterRule(value?: string) {
    return value
        ?.replace(/([A-Z])/g, " $1")
        .trim()
        .toLowerCase()
}

export function isValidEmail(email: string) {
    const emailPattern = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/
    return emailPattern.test(email)
}

export function isValidPhoneNumber(phoneNumber: string) {
    const phoneNumberPattern = /^\+?[1-9]\d{1,14}$/
    return phoneNumberPattern.test(phoneNumber)
}

export function cn(defaultClasses: string, customClasses?: string) {
    return twMerge(defaultClasses, customClasses)
}

export function getNotFoundText(resource: string, searchTerm?: string) {
    if (!searchTerm) {
        return `No ${resource} found`
    }
    return `No ${resource} match "${searchTerm}"`
}

export function numberWithCommas(x: number) {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
}

export function getSendTypeForWorkflow(type: string) {
    switch (type) {
        case SendEmailToType.enrolled:
            return "Enrolled contacts"
        case SendEmailToType.specificEmail:
            return "Specific email"
        case SendEmailToType.specificUsers:
            return "Specific users"
    }
}

export function getTaskStage(task: Task): string {
    const now = new Date()
    const startAt = new Date(task.when?.startAt)
    const diffDays = (startAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)

    const shouldMarkAsMissed = task.shouldMarkAsMissedIfNotCompleted
    const units = parseInt(task.unitsOfTimeToMarkAsMissed || "30")
    const unitType = task.typeOfTimeToMarkAsMissed || "days"

    let markAsMissedAt: Date | null = null

    if (shouldMarkAsMissed) {
        let multiplier: number

        switch (unitType) {
            case "minutes":
                multiplier = 60 * 1000
                break
            case "hours":
                multiplier = 60 * 60 * 1000
                break
            case "days":
            default:
                multiplier = 24 * 60 * 60 * 1000
                break
        }

        markAsMissedAt = new Date(startAt.getTime() + units * multiplier)
    }

    if (task.status === "completed") return "Completed"
    if (task.status === "skipped") return "Skipped"

    if (shouldMarkAsMissed && markAsMissedAt && now > markAsMissedAt) {
        return "Missed"
    }

    if (diffDays > 30) return "Future"
    if (diffDays > 1) return "Upcoming"
    if (diffDays >= -1 && diffDays <= 1) return "Due"
    if (diffDays < -1) return "Overdue"

    return "Unknown"
}

export function getStageColor(stage: string): string {
    switch (stage) {
        case "Future":
            return "#DBEAFE" // blue-100
        case "Upcoming":
            return "#FEF3C7" // yellow-100
        case "Due":
            return "#FFEDD5" // orange-100
        case "Overdue":
            return "#FEE2E2" // red-100
        case "Missed":
            return "#FCE7F3" // pink-100
        case "Completed":
            return "#D1FAE5" // green-100
        case "Skipped":
            return "#E0F2FE" // light-blue-100
        default:
            return "#F3F4F6" // gray-100
    }
}

export function getStageTextColor(stage: string): string {
    switch (stage) {
        case "Future":
            return "#1E40AF" // blue-800
        case "Upcoming":
            return "#92400E" // yellow-800
        case "Due":
            return "#9A3412" // orange-800
        case "Overdue":
            return "#991B1B" // red-800
        case "Missed":
            return "#9D174D" // pink-800
        case "Completed":
            return "#065F46" // green-800
        case "Skipped":
            return "#075985" // light-blue-800
        default:
            return "#282B34" // gray-800
    }
}

export function toPascalCase(str: string) {
    return str
        .replace(/(\w)(\w*)/g, function (_, g1, g2) {
            return g1.toUpperCase() + g2.toLowerCase()
        })
        .replace(/\s+/g, "")
}

export async function transformEmailBody(
    body: string,
    uploadImageToS3: (file: File) => Promise<string>
): Promise<string> {
    const parser = new DOMParser()
    const doc = parser.parseFromString(body, "text/html")

    // Helper function to decode HTML entities
    function decodeHtmlEntities(str: string): string {
        const parser = new DOMParser()
        const decoded = parser.parseFromString(str, "text/html").documentElement.textContent
        return decoded || str
    }

    // Process all images in the body, including those inside tables
    const images = doc.querySelectorAll("img")
    for (const img of images) {
        const src = img.getAttribute("src")
        if (src && src.startsWith("data:image/")) {
            console.log("Processing image:", src)

            // Decode base64 image data
            const [metadata, base64Data] = src.split(",")
            const mimeType = metadata.match(/:(.*?);/)?.[1] || "image/jpeg" // Default to "image/jpeg"
            const binaryString = atob(base64Data)
            console.log("Decoded image size (bytes):", binaryString.length)

            const canvas = document.createElement("canvas")
            const context = canvas.getContext("2d")
            const image = new Image()

            // Wait for the image to load
            await new Promise<void>((resolve) => {
                image.onload = async () => {
                    const isLargeImage = binaryString.length > 1 * 1024 * 1024 // 1MB limit
                    let file: File

                    if (isLargeImage) {
                        console.log("Resizing image...")

                        const maxDimension = 800 // Resize to fit within max 800px width or height
                        let width = image.width
                        let height = image.height

                        console.log("Original image dimensions:", width, "x", height)

                        if (width > maxDimension || height > maxDimension) {
                            if (width > height) {
                                height = (maxDimension / width) * height
                                width = maxDimension
                            } else {
                                width = (maxDimension / height) * width
                                height = maxDimension
                            }
                        }

                        // Resize the image on a canvas
                        canvas.width = width
                        canvas.height = height
                        context?.drawImage(image, 0, 0, width, height)

                        console.log("Resized image dimensions:", width, "x", height)

                        // Convert the canvas to a Blob for S3 upload
                        file = await new Promise<File>((blobResolve) => {
                            canvas.toBlob(
                                (blob) => {
                                    if (blob) {
                                        blobResolve(new File([blob], "optimized-image.jpg", { type: "image/jpeg" }))
                                    }
                                },
                                "image/jpeg",
                                0.7 // Compress to 70% quality
                            )
                        })
                    } else {
                        console.log("Uploading original image (no resize)...")

                        // Decode the Base64 string into binary data
                        const binaryData = atob(base64Data)
                        const arrayBuffer = new Uint8Array(binaryData.length)

                        for (let i = 0; i < binaryData.length; i++) {
                            arrayBuffer[i] = binaryData.charCodeAt(i)
                        }

                        // Create a Blob from the binary data
                        const blob = new Blob([arrayBuffer], { type: mimeType })

                        // Create a File from the Blob
                        file = new File([blob], "original-image.jpg", { type: mimeType })
                    }

                    console.log("Uploading image to S3:", file)

                    // Upload to S3 and replace the src with the returned URL
                    const uploadedUrl = await uploadImageToS3(file)
                    const decodedUrl = decodeHtmlEntities(uploadedUrl) // Decode HTML entities in the URL
                    img.setAttribute("src", decodedUrl)
                    resolve()
                }

                image.onerror = () => {
                    console.error("Failed to load image:", src)
                    resolve()
                }

                image.src = src
            })
        }
    }

    // Transform YouTube iframes into clickable thumbnails
    const iframes = doc.querySelectorAll("iframe")
    iframes.forEach((iframe) => {
        const src = iframe.getAttribute("src")
        if (src && src.includes("youtube.com/embed/")) {
            const videoId = src.split("embed/")[1].split("?")[0]

            const link = doc.createElement("a")
            link.href = `https://www.youtube.com/watch?v=${videoId}`
            link.target = "_blank"

            const img = doc.createElement("img")
            img.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
            img.alt = "Watch the video on YouTube"
            img.style.maxWidth = "100%"
            img.style.height = "auto"
            img.style.border = "none"
            img.style.display = "block"

            link.appendChild(img)
            iframe.parentNode?.replaceChild(link, iframe)
        }
    })

    // Process all tables for better compatibility
    const tables = doc.querySelectorAll("table")
    tables.forEach((table) => {
        // Remove unsupported colgroup elements
        const colgroups = table.querySelectorAll("colgroup")
        colgroups.forEach((colgroup) => colgroup.remove())

        // Apply inline styles for email compatibility
        table.setAttribute("border", "1")
        table.setAttribute("cellpadding", "10")
        table.setAttribute("cellspacing", "0")
        table.style.borderCollapse = "collapse"
        table.style.width = "100%"
        table.style.border = "1px solid #ddd"
        table.style.marginBottom = "16px"

        const ths = table.querySelectorAll("th")
        ths.forEach((th) => {
            th.style.border = "1px solid #ddd"
            th.style.padding = "12px"
            th.style.backgroundColor = "#f0f0f0"
            th.style.textAlign = "left"
            th.style.fontWeight = "bold"
        })

        const tds = table.querySelectorAll("td")
        tds.forEach((td) => {
            td.style.border = "1px solid #ddd"
            td.style.padding = "12px"
            td.style.textAlign = "center"
        })

        const imgs = table.querySelectorAll("img")
        imgs.forEach((img) => {
            img.width = 320
            img.height = 180
            img.style.maxWidth = "100%"
            img.style.display = "block"
            img.style.border = "1px solid #ccc"
        })
    })

    return doc.body.innerHTML
}

export function convertAlertTrigger(trigger: AlertTrigger) {
    switch (trigger) {
        case AlertTrigger.newListing:
            return "New listing"
        case AlertTrigger.newTourInsights:
            return "New tour insights"
        case AlertTrigger.openHouseUpdates:
            return "Open house updates"
        case AlertTrigger.priceChange:
            return "Price change"
        case AlertTrigger.statusChange:
            return "Status change"
        case AlertTrigger.soldListing:
            return "Sold listing"
        default:
            return ""
    }
}

export function getTrackerDisplayEventName(name: string) {
    switch (name) {
        case "page_view":
            return "Page view"
        case "signed_in":
            return "Signed in"
        case "requested_showing":
            return "Requested showing"
        case "saved_listing":
            return "Saved listing"
        case "favorited_listing":
            return "Favorited listing"
        case "hidden_listing":
            return "Hidden listing"
    }
}

export function getWidgetIcon(label: string) {
    switch (label) {
        case "buyerQualificationWidget":
            return BulletListWithTwoCheckmarkIcon
        case "leftPanelWidget":
            return UserIcon
        case "contactListings":
            return ClipboardListIcon
        case "doNotDisturbWidget":
            return DNDIcon
        case "filesViewWidget":
            return PaperClipIcon
        case "lenderInformationWidget":
            return IdentificationIcon
        case "savedSearches":
            return BellIcon
        case "marketingPreferencesWidget":
            return AdjustmentsIcon
        case "sellerQualificationWidget":
            return NumberedListIcon
        case "tagsViewWidget":
            return TagIcon
        case "tasksViewWidget":
            return ClipboardCheckIcon
        case "middlePanelWidget":
            return CalendarIcon
        case "workflowsWidget":
            return FireIcon
        case "marketingCampaignsWidget":
            return SpeakerphoneIcon
        default:
            return UserIcon
    }
}

export function generateFilterSummary(filtersGroups: FiltersGroup[]): string {
    const ruleDescriptions: Record<string, string> = {
        contains: "∋",
        doesNotContain: "∌",
        isNotEmpty: "¬Ø",
        isEmpty: "Ø",
        isLessThan: "<",
        isGreaterThan: ">",
        isLessThanOrEqualTo: "≤",
        isGreaterThanOrEqualTo: "≥",
        isBetween: ">",
        isNotBetween: "!>",
        isEqualTo: "=",
        isNotEqualTo: "!=",
        isIn: "∈",
        isNotIn: "∉",
    }

    if (!filtersGroups || filtersGroups.length === 0) {
        return ""
    }

    return filtersGroups
        .map(
            (group) =>
                group.filters
                    .map((filter) => {
                        const fieldLabel = filter.displayName || filter.field
                        const operator = ruleDescriptions[filter.rule || 0] || filter.rule

                        // Handle array values like ranges
                        const value = Array.isArray(filter.value)
                            ? filter.rule === "isBetween" || filter.rule === "isNotBetween"
                                ? `${filter.value[0]}<${filter.value[1]}`
                                : `[${filter.value.join(",")}]`
                            : filter.value

                        // Construct summary
                        if (filter.rule === "isEmpty" || filter.rule === "isNotEmpty") {
                            return `${fieldLabel}${operator}`
                        }

                        return `${fieldLabel}${operator}${value}`
                    })
                    .join(", ") // Join filters in the same group with a comma
        )
        .join(" OR ") // Join groups with OR for readability
}

// Pad single digit numbers with a leading zero
export function pad(number: number) {
    // If the number is less than 10, prepend a '0'. Otherwise, return the number as is.
    return number < 10 ? "0" + number : number
}

export function handleImageError(event: Event, media: any[]) {
    const img = event.target as HTMLImageElement
    const currentSrc = img.src
    const currentIndex = media.findIndex((m) => m.mediaURL === currentSrc)

    if (currentIndex !== -1 && currentIndex + 1 < media.length) {
        // Try the next image in the array
        img.src = media[currentIndex + 1]?.mediaURL
    } else {
        // If no more images or current image not found, use fallback
        img.src = "@/assets/icons/listing-photo-fallback.png"
    }
}

export function getIconByType(type: string, name: string) {
    switch (type.toLowerCase()) {
        case AttributeType.Date:
            return CalendarIcon
        case AttributeType.String:
            if (name === "owner" || name === "agent" || name === "data.command.query.createdById" || name === "contact")
                return UserCircleIcon
            if (name === "email") return AtSymbolIcon
            if (name === "{templateRenderedSignature}") return PencilIcon
            return StringTypeIcon
        case AttributeType.Strings:
            return StringTypeIcon
        case AttributeType.Number:
            if (name === "price" || name === "invoicePrice" || name.toLowerCase().includes("price"))
                return CurrencyDollarIcon
            return HashtagIcon
        case AttributeType.Boolean:
            return BooleanTypeToggleIcon
        case AttributeType.Tag:
            return TagIcon
        case AttributeType.Tags:
            return TagIcon
        case AttributeType.Origin:
            return LinkIcon
        case AttributeType.Phones:
            return PhoneIcon
        case AttributeType.Emails:
            return AtSymbolIcon
        case AttributeType.EmailMessaging:
            return ChatAltIcon
        case AttributeType.InvoicePrice:
            return CurrencyDollarIcon
        case AttributeType.SubscriptionPlan:
            return CollectionIcon
        case AttributeType.Object:
            if (name === "agent") return UserCircleIcon
            if (name === "contact") return UserCircleIcon
            if (name === "createdBy") return UserCircleIcon
            if (name === "fromPhoneNumber") return PhoneIcon
            if (name === "lead") return UserIcon
            if (name === "properties") return OfficeBuildingIcon
            if (name === "address") return HomeIcon
            if (name === "agencyPhoneNumber") return PhoneIcon
            if (name === "acl") return ShareIcon
            break
        case AttributeType.Objects:
            if (name === "leads") return UserIcon
            if (name === "messages") return ChatAltIcon
            if (name === "agencyDomains") return GlobeAltIcon
            if (name === "agencyOwners" || name === "agents") return UserCircleIcon
            break
        case AttributeType.Groups:
            return CollectionIcon
    }
}

export function formatCurrency(value: number): string {
    return `$${numberWithCommas(Math.round(value))}`
}
